import { mergeWith } from 'lodash';
import { normalize } from 'normalizr';
import { createReducer } from '@reduxjs/toolkit';

import { replaceArrays } from 'shared/lodash-utils';
import {
  addCreatedSessionActionItem,
  createMentoringProgramSession,
  deleteMentoringProgramSession,
  fetchMeetingPlan,
  getMentoringProgramSessions,
  getSingleMentoringProgramSession,
  removeDeletedSessionActionItem,
  resetMentoringProgramNextSteps,
  resetMentoringProgramSessionsList,
  updateMentoringProgramSession,
  updateModifiedSessionActionItem,
} from 'redux/actions/mentoring-program-sessions';
import { MentorshipProgramSessionsSchema } from 'redux/schemas/api/mentoring-program-sessions';
import { CompletionStatus } from 'redux/schemas/models/mentoring-program-enrollments';
import { Cadence } from 'redux/schemas/models/mentoring-program-sessions';
import { initialRootState } from '.';

export default createReducer(initialRootState, builder => {
  builder
    .addCase(getMentoringProgramSessions.fulfilled, (state, action) => {
      const normalized = normalize(action.payload, MentorshipProgramSessionsSchema);
      state.app.mentoringProgram.sessions.hasBeenLoaded = true;

      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(getSingleMentoringProgramSession.fulfilled, (state, action) => {
      const session = action.payload;
      state.models.mentorshipProgramSessions[session.id] = session;
    })
    .addCase(createMentoringProgramSession.pending, (state) => {
      state.app.mentoringProgram.sessions.updateSession.isLoading = true;
    })
    .addCase(createMentoringProgramSession.fulfilled, (state, action) => {
      const normalized = normalize(action.payload, MentorshipProgramSessionsSchema);

      mergeWith(state.models, normalized.entities, replaceArrays);

      const { enrollmentId, mentorshipProgramConnectionId } = action.meta.arg;
      const connectionIndex = state.models.mentoringProgramEnrollments[enrollmentId].assignedConnections.findIndex((c) => c.connectionId === mentorshipProgramConnectionId);
      if (!state.models.mentoringProgramEnrollments[enrollmentId].assignedConnections[connectionIndex].states) {
        state.models.mentoringProgramEnrollments[enrollmentId].assignedConnections[connectionIndex].states = { profileCompletion: { status: null }, profileReview: { status: null }, waitingConnection: { status: null }, waitingFirstSession: { status: null } };
      }
      if (!state.models.mentoringProgramEnrollments[enrollmentId].assignedConnections[connectionIndex].states?.waitingFirstSession) {
        state.models.mentoringProgramEnrollments[enrollmentId].assignedConnections[connectionIndex].states.waitingFirstSession = { status: null };
      }
      state.models.mentoringProgramEnrollments[enrollmentId].assignedConnections[connectionIndex].states.waitingFirstSession.status = CompletionStatus.COMPLETED;
      state.models.mentoringProgramEnrollments[enrollmentId].assignedConnections[connectionIndex].isSessionCreated = true;
      state.app.mentoringProgram.sessions.updateSession.isLoading = false;
    })
    .addCase(updateMentoringProgramSession.pending, (state) => {
      state.app.mentoringProgram.sessions.updateSession.isLoading = true;
    })
    .addCase(updateMentoringProgramSession.fulfilled, (state, action) => {
      state.app.mentoringProgram.sessions.updateSession.isLoading = false;
    })
    .addCase(deleteMentoringProgramSession.fulfilled, (state, action) => {
      const { sessionId } = action.meta.arg;
      delete state.models.mentorshipProgramSessions[sessionId];
    })
    .addCase(resetMentoringProgramSessionsList, (state, action) => {
      const { resetList } = action.payload;
      state.app.mentoringProgram.sessions.resetList = resetList;
    })
    .addCase(addCreatedSessionActionItem, (state, action) => {
      const { sessionId, actionItem } = action.payload;
      const session = state.models.mentorshipProgramSessions[sessionId];
      if (session) {
        session.mentorshipProgramActionItems = [
          actionItem,
          ...session.mentorshipProgramActionItems,
        ];
      }
    })
    .addCase(updateModifiedSessionActionItem, (state, action) => {
      const { sessionId, actionItem } = action.payload;
      const session = state.models.mentorshipProgramSessions[sessionId];
      if (session) {
        const index = session.mentorshipProgramActionItems.findIndex(
          (item) => item.id === actionItem.id,
        );
        session.mentorshipProgramActionItems[index] = actionItem;
      }
    })
    .addCase(removeDeletedSessionActionItem, (state, action) => {
      const { sessionId, actionItemId } = action.payload;
      const session = state.models.mentorshipProgramSessions[sessionId];
      if (session) {
        let sessionActionItems = session.mentorshipProgramActionItems;
        sessionActionItems = sessionActionItems.filter(
          (item) => item.id !== actionItemId,
        );
        state.models.mentorshipProgramSessions[
          sessionId
        ].mentorshipProgramActionItems = sessionActionItems;
      }
    })
    .addCase(fetchMeetingPlan.fulfilled, (state, action) => {
      const { mentorshipProgramConnectionId } = action.meta.arg;
      state.models.mentorshipProgramMeetingPlans[mentorshipProgramConnectionId] = action.payload[0] ?? null;
    })
    .addCase(resetMentoringProgramNextSteps, (state, action) => {
      const { resetNextSteps } = action.payload;
      state.app.mentoringProgram.nextSteps.resetNextSteps = resetNextSteps;
    });
});
